import React, { useState } from "react";
import { TextField, Button } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar";
import "../../styles/Style.css";
import "./GeradorSerial.css";

const ROTA_IIS = "/SAGA_IIS/gerar";

// O IIS tem 24 dígitos: embalagem(1) país(3) fabricante(4) produto(5) sequencial(10) dv(1).
// Quebra o que a rota devolveu, em vez de recalcular no front — assim a tela nunca
// divergir do back.
const quebrarIIS = (iis) => {
  if (!iis || iis.length !== 24) return [];

  return [
    ["Embalagem", iis.slice(0, 1)],
    ["País", iis.slice(1, 4)],
    ["Fabricante", iis.slice(4, 8)],
    ["Produto", iis.slice(8, 13)],
    ["Sequencial", iis.slice(13, 23)],
    ["DV", iis.slice(23)]
  ];
};

const mensagemErro = (error) => {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) return data;
  if (data?.title) return data.title;
  if (error?.response?.status === 404) return "Ordem ou lote não encontrado.";

  return "Não foi possível consultar o IIS.";
};

export default function GeradorSerial() {
  const [identificador, setIdentificador] = useState("");
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const buscar = async () => {
    const chave = identificador.trim();

    if (!chave) {
      toast.error("Informe a ordem ou o lote.");
      return;
    }

    setCarregando(true);
    setErro("");
    setResultado(null);

    try {
      const { data } = await blogFetch.get(`${ROTA_IIS}/${encodeURIComponent(chave)}`);
      setResultado(data);
    } catch (error) {
      console.error(error);
      setErro(mensagemErro(error));
    } finally {
      setCarregando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(resultado.iis);
      toast.success("IIS copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const partes = quebrarIIS(resultado?.iis);

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className="app_body">
        <div className="gerador_serial">
          <div>
            <h1 className="gerador_serial_titulo">Gerador de Serial (IIS)</h1>
            <p className="gerador_serial_subtitulo">
              Informe a ordem ou o lote. O IIS vem do back, que devolve o código já
              existente ou gera na hora.
            </p>
          </div>

          <TextField
            label="Ordem ou lote"
            fullWidth
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "NumpadEnter") buscar();
            }}
            placeholder="Leia ou digite a ordem ou o lote"
            autoFocus
            disabled={carregando}
            helperText="A ordem tem prioridade na resolução."
            inputProps={{ style: { fontSize: 22, fontFamily: '"Courier New", monospace' } }}
          />

          <Button
            variant="contained"
            onClick={buscar}
            disabled={carregando}
            style={{ alignSelf: "flex-start" }}
          >
            {carregando ? <Spinner animation="border" size="sm" /> : "Gerar"}
          </Button>

          {erro ? <div className="gerador_serial_erro">{erro}</div> : null}

          {resultado ? (
            <div className="gerador_serial_resultado">
              <span
                className={`gerador_serial_origem ${
                  resultado.origem === "VIEW"
                    ? "gerador_serial_origem_view"
                    : "gerador_serial_origem_gerado"
                }`}
              >
                {resultado.origem === "VIEW" ? "Já existia (view)" : "Gerado agora"}
              </span>

              <div className="gerador_serial_partes">
                <div>
                  <div className="gerador_serial_parte_rotulo">Ordem</div>
                  <div className="gerador_serial_parte_valor">{resultado.ordem || "—"}</div>
                </div>
                <div>
                  <div className="gerador_serial_parte_rotulo">Lote</div>
                  <div className="gerador_serial_parte_valor">{resultado.lote || "—"}</div>
                </div>
                <div>
                  <div className="gerador_serial_parte_rotulo">Serial completo</div>
                  <div className="gerador_serial_parte_valor">
                    {resultado.serialCompleto || "—"}
                  </div>
                </div>
              </div>

              <span className="gerador_serial_iis">{resultado.iis}</span>

              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={copiar}
                style={{ alignSelf: "flex-start" }}
              >
                Copiar
              </Button>

              {partes.length ? (
                <div className="gerador_serial_partes">
                  {partes.map(([rotulo, valor]) => (
                    <div key={rotulo}>
                      <div className="gerador_serial_parte_rotulo">{rotulo}</div>
                      <div className="gerador_serial_parte_valor">{valor}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
